import { Link, useMatchRoute, useNavigate } from '@tanstack/react-router';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PeopleIcon from '@mui/icons-material/People';
import PublicIcon from '@mui/icons-material/Public';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import SettingsIcon from '@mui/icons-material/Settings';
import BugReportIcon from '@mui/icons-material/BugReport';
import MenuIcon from '@mui/icons-material/Menu';
import TokenIcon from '@mui/icons-material/Token';
import TimelineIcon from '@mui/icons-material/Timeline';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FolderIcon from '@mui/icons-material/Folder';
import { useUiStore } from '../../stores/ui.store';
import { useCreditsBalance } from '../../api/queries/credits';
import { useProjects, useProject } from '../../api/queries/projects';
import { useProjectStore } from '../../stores/project.store';

interface NavItem {
  to: string;
  label: string;
  icon: typeof DashboardIcon;
  exact?: boolean;
  disabled?: boolean;
}

const LIBRARY_ITEMS: NavItem[] = [
  { to: '/projects', label: 'All Projects', icon: FolderIcon },
  { to: '/series', label: 'Series', icon: CollectionsBookmarkIcon },
  { to: '/entities', label: 'All Entities', icon: PeopleIcon },
  { to: '/worlds', label: 'All Worlds', icon: PublicIcon },
  {
    to: '/reference-library',
    label: 'Reference Library',
    icon: LibraryBooksIcon,
  },
  { to: '/images', label: 'Media Library', icon: PhotoLibraryIcon },
  { to: '/training-queue', label: 'Training Queue', icon: PsychologyIcon },
];

const DEBUG_ITEMS: NavItem[] = [
  { to: '/debug/ai-actions', label: 'AI Actions', icon: BugReportIcon },
];

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 64;

function renderNavItem(
  item: NavItem,
  collapsed: boolean,
  isActive: (to: string, exact?: boolean) => boolean,
) {
  const active = isActive(item.to, item.exact);
  const Icon = item.icon;
  const button = (
    <ListItemButton
      key={item.to}
      component={Link}
      to={item.to}
      selected={active}
      disabled={item.disabled}
      sx={{
        mx: 1,
        borderRadius: 1,
        mb: 0.25,
        minHeight: 44,
        justifyContent: collapsed ? 'center' : 'flex-start',
        px: collapsed ? 1 : 2,
        borderLeft: active ? '3px solid' : '3px solid transparent',
        borderLeftColor: active ? 'primary.main' : 'transparent',
        fontWeight: active ? 700 : 400,
        bgcolor: active ? 'action.selected' : 'transparent',
        '&:hover': {
          bgcolor: active ? 'action.selected' : 'action.hover',
        },
        opacity: item.disabled ? 0.4 : 1,
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: collapsed ? 0 : 36,
          color: active ? 'primary.main' : 'text.secondary',
          justifyContent: 'center',
        }}
      >
        <Icon fontSize="small" />
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: 14,
            fontWeight: active ? 700 : 400,
            noWrap: true,
          }}
        />
      )}
    </ListItemButton>
  );
  return collapsed ? (
    <Tooltip
      key={item.to}
      title={item.disabled ? `${item.label} (unavailable)` : item.label}
      placement="right"
      arrow
    >
      <span>{button}</span>
    </Tooltip>
  ) : (
    button
  );
}

export function Sidebar() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const { data: creditsData } = useCreditsBalance();
  const { data: projects = [] } = useProjects();
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const setActiveProject = useProjectStore((s) => s.setActiveProject);
  const { data: activeProject } = useProject(activeProjectId ?? undefined);

  const isActive = (to: string, exact?: boolean) => {
    if (exact) {
      return !!matchRoute({ to, fuzzy: false });
    }
    return !!matchRoute({ to, fuzzy: true });
  };

  const width = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  // Build project-scoped nav items
  const primaryBook = activeProject?.books?.find((b) => b.isPrimary);
  const primaryOutline = activeProject?.outlines?.find((o) => o.isPrimary);

  const projectNavItems: NavItem[] = activeProjectId
    ? [
        {
          to: `/projects/${activeProjectId}`,
          label: 'Dashboard',
          icon: DashboardIcon,
          exact: true,
        },
        {
          to: primaryBook ? `/books/${primaryBook.id}` : '#book-editor',
          label: 'Book Editor',
          icon: MenuBookIcon,
          disabled: !primaryBook,
        },
        {
          to: primaryOutline ? `/outlines/${primaryOutline.id}` : '#outline',
          label: 'Outline',
          icon: AccountTreeIcon,
          disabled: !primaryOutline,
        },
        {
          to: activeProject?.sessionId
            ? `/story-studio/${activeProject.sessionId}`
            : '#story-studio',
          label: 'Story Studio',
          icon: AutoStoriesIcon,
          disabled: !activeProject?.sessionId,
        },
        {
          to: activeProject?.architectId
            ? `/story-architect/${activeProject.architectId}`
            : '#story-architect',
          label: 'Story Architect',
          icon: TimelineIcon,
          disabled: !activeProject?.architectId,
        },
        {
          to: activeProject?.boardId
            ? `/boards/${activeProject.boardId}`
            : '#board',
          label: 'Creativity Board',
          icon: ViewKanbanIcon,
          disabled: !activeProject?.boardId,
        },
        { to: '/worlds', label: 'Worlds', icon: PublicIcon },
        { to: '/entities', label: 'Entities', icon: PeopleIcon },
      ]
    : [];

  const handleProjectChange = (event: SelectChangeEvent<string>) => {
    const val = event.target.value;
    if (val === '__manage__') {
      navigate({ to: '/projects' });
      return;
    }
    setActiveProject(val || null);
    if (val) {
      navigate({ to: '/projects/$projectId', params: { projectId: val } });
    }
  };

  return (
    <Box
      component="aside"
      sx={{
        width,
        minWidth: width,
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        transition: 'width 200ms ease, min-width 200ms ease',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: collapsed ? 1 : 2,
          py: 1.5,
          minHeight: 56,
          justifyContent: collapsed ? 'center' : 'space-between',
        }}
      >
        {!collapsed && (
          <Typography
            variant="subtitle1"
            fontWeight={700}
            noWrap
            sx={{ letterSpacing: -0.3 }}
          >
            Immersive Books
          </Typography>
        )}
        <IconButton
          size="small"
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          <MenuIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      {/* Project Selector */}
      {!collapsed ? (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Select
            value={activeProjectId ?? ''}
            onChange={handleProjectChange}
            displayEmpty
            fullWidth
            size="small"
            sx={{ fontSize: 14 }}
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    Select a project...
                  </Typography>
                );
              }
              const proj = projects.find((p) => p.id === selected);
              return (
                <Typography variant="body2" noWrap>
                  {proj?.name ?? 'Unknown'}
                </Typography>
              );
            }}
          >
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                <Typography variant="body2" noWrap>
                  {p.name}
                </Typography>
              </MenuItem>
            ))}
            <Divider />
            <MenuItem value="__manage__">
              <Typography variant="body2" color="primary" fontWeight={600}>
                Manage Projects
              </Typography>
            </MenuItem>
          </Select>
        </Box>
      ) : (
        <Tooltip title="Projects" placement="right" arrow>
          <IconButton
            size="small"
            sx={{ mx: 'auto', my: 1 }}
            onClick={() => navigate({ to: '/projects' })}
          >
            <FolderIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Divider />

      {/* Main nav */}
      <List sx={{ flex: 1, py: 1, overflowY: 'auto' }}>
        {/* Project-scoped nav */}
        {activeProjectId && projectNavItems.length > 0 && (
          <>
            {!collapsed && (
              <ListSubheader
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.8,
                  lineHeight: '32px',
                  bgcolor: 'transparent',
                }}
              >
                Project
              </ListSubheader>
            )}
            {collapsed && <Divider sx={{ my: 0.5, mx: 1 }} />}
            {projectNavItems.map((item) =>
              renderNavItem(item, collapsed, isActive),
            )}
          </>
        )}

        {!activeProjectId && (
          <>
            {!collapsed && (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Select or create a project to see project tools.
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Library section */}
        {!collapsed && (
          <ListSubheader
            sx={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              lineHeight: '32px',
              bgcolor: 'transparent',
              mt: 1,
            }}
          >
            Library
          </ListSubheader>
        )}
        {collapsed && <Divider sx={{ my: 0.5, mx: 1 }} />}
        {LIBRARY_ITEMS.map((item) => renderNavItem(item, collapsed, isActive))}

        {/* Settings */}
        {!collapsed && <Divider sx={{ my: 0.5, mx: 1 }} />}
        {collapsed && <Divider sx={{ my: 0.5, mx: 1 }} />}
        {renderNavItem(
          { to: '/settings', label: 'Settings', icon: SettingsIcon },
          collapsed,
          isActive,
        )}
      </List>

      {/* Debug section */}
      <Divider />
      <List dense sx={{ py: 0.5 }}>
        {DEBUG_ITEMS.map((item) => {
          const active = isActive(item.to);
          const Icon = item.icon;
          const button = (
            <ListItemButton
              key={item.to}
              component={Link}
              to={item.to}
              selected={active}
              sx={{
                mx: 1,
                borderRadius: 1,
                minHeight: 40,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 1 : 2,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: collapsed ? 0 : 36,
                  color: 'text.secondary',
                  justifyContent: 'center',
                }}
              >
                <Icon fontSize="small" />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 13, noWrap: true }}
                />
              )}
            </ListItemButton>
          );
          return collapsed ? (
            <Tooltip key={item.to} title={item.label} placement="right" arrow>
              {button}
            </Tooltip>
          ) : (
            button
          );
        })}
      </List>

      {/* Footer — Credits */}
      <Divider />
      <Box
        sx={{
          px: collapsed ? 1 : 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 1,
        }}
      >
        <TokenIcon fontSize="small" sx={{ color: 'warning.main' }} />
        {!collapsed && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {creditsData?.balance != null
              ? `${creditsData.balance.toLocaleString()} credits`
              : '-- credits'}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
